import { motion } from "framer-motion";
import { Clock, User2, Instagram, Download, Loader2, Film, BookOpen, Image as ImageIcon } from "lucide-react";
import { formatDuration, formatUploadDate } from "../lib/format";
import { MetadataEditor } from "./MetadataEditor";
import { HOME } from "../constants/testIds";

const kindLabel = {
  reel: { label: "REEL", icon: Film },
  story: { label: "STORY", icon: BookOpen },
  post: { label: "POST", icon: Instagram },
};

export const PreviewCard = ({ preview, meta, setMeta, defaults, onReset, onRandom, onDownload, downloading }) => {
  const isImage = preview.media_type === "image";
  const Kind = isImage
    ? { label: "PHOTO", icon: ImageIcon }
    : kindLabel[preview.kind] || kindLabel.reel;
  const KIcon = Kind.icon;

  return (
    <motion.div
      data-testid={HOME.previewCard}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 12 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-4xl mx-auto"
    >
      <div className="relative rounded-3xl border border-white/10 bg-[#0a0a0b]/80 backdrop-blur-2xl overflow-hidden shadow-[0_30px_120px_-30px_rgba(220,39,67,0.4)]">
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-pink-600/20 rounded-full blur-3xl pointer-events-none" />

        <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
          <div className="md:col-span-2 relative bg-black">
            <div className="aspect-[9/16] md:aspect-auto md:h-full w-full overflow-hidden">
              {preview.thumbnail ? (
                <img
                  src={preview.thumbnail}
                  alt={preview.title}
                  className="w-full h-full object-cover"
                  crossOrigin="anonymous"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-700">
                  <Instagram className="w-16 h-16" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
            </div>
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 bg-black/60 backdrop-blur-md border border-white/15 rounded-full px-3 py-1 text-[10px] font-bold tracking-widest">
                <KIcon className="w-3 h-3" />
                {Kind.label}
              </span>
            </div>
          </div>

          <div className="md:col-span-3 p-6 sm:p-8">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight leading-tight">
              {preview.title || "Instagram видео"}
            </h2>

            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-zinc-400">
              {preview.uploader && (
                <span className="inline-flex items-center gap-1.5">
                  <User2 className="w-4 h-4" />@{preview.uploader}
                </span>
              )}
              {!isImage && (
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  {formatDuration(preview.duration)}
                </span>
              )}
              {preview.upload_date && (
                <span className="text-zinc-500">
                  {formatUploadDate(preview.upload_date)}
                </span>
              )}
            </div>

            <MetadataEditor
              meta={meta}
              setMeta={setMeta}
              defaults={defaults}
              onReset={onReset}
              onRandom={onRandom}
            />

            <button
              data-testid={HOME.downloadButton}
              onClick={onDownload}
              disabled={downloading}
              className="mt-8 w-full ig-gradient animated-gradient text-white font-semibold rounded-2xl px-6 py-4 flex items-center justify-center gap-3 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed transition-transform duration-200 shadow-[0_15px_50px_-15px_rgba(220,39,67,0.7)]"
            >
              {downloading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Готовлю файл...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  {isImage ? "Скачать фото" : "Скачать MP4"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
